import React, { useEffect, useState } from "react";
import { dms, inr, niceDate } from "./api";
import { PageHeader } from "./OwnerPages";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LedgerEntryDialog } from "./ReturnDetail";

const CREDIT_KINDS = ["payment", "coupon_credit", "credit_note", "sale_return"];
const KIND_LABELS = { invoice: "Sale", debit_note: "Debit Note", payment: "Payment", credit_note: "Credit Note", coupon_credit: "Coupon Credit", sale_return: "Sale Return" };

function LedgerTable({ entries, debtorView = false }) {
  const [retDetail, setRetDetail] = React.useState(null);
  const withBalance = React.useMemo(() => {
    const asc = [...entries].sort((a, b) => new Date(a.at) - new Date(b.at));
    let bal = 0;
    const balById = new Map();
    asc.forEach((e) => {
      bal += CREDIT_KINDS.includes(e.kind) ? -e.amount : e.amount;
      balById.set(e.id, bal);
    });
    return entries.map(e => ({ ...e, _balance: balById.get(e.id) ?? 0 }));
  }, [entries]);

  return (
    <Card>
      <LedgerEntryDialog entry={retDetail} onClose={() => setRetDetail(null)} debtorView={debtorView} />
      <div className="p-4 border-b border-slate-100 font-semibold flex items-center justify-between">
        <span>Transactions</span>
        {entries.length > 0 && <span className="text-xs text-slate-400">{entries.length} entries</span>}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Type</TableHead>
            <TableHead>Number</TableHead>
            <TableHead>Date</TableHead>
            <TableHead className="text-right">Total</TableHead>
            <TableHead className="text-right">Balance</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {withBalance.map(e => (
            <TableRow key={e.id} onClick={() => setRetDetail(e)} className="cursor-pointer hover:bg-slate-50">
              <TableCell><span className={`text-xs px-2 py-0.5 rounded ${CREDIT_KINDS.includes(e.kind) ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{KIND_LABELS[e.kind] || e.kind}</span></TableCell>
              <TableCell className="font-mono text-xs">{e.reference_no}</TableCell>
              <TableCell className="text-xs text-slate-500">{niceDate(e.at)}</TableCell>
              <TableCell className={`text-right font-semibold ${CREDIT_KINDS.includes(e.kind) ? "text-emerald-700" : "text-rose-700"}`}>{CREDIT_KINDS.includes(e.kind) !== debtorView ? "-" : "+"}{inr(e.amount)}</TableCell>
              <TableCell className="text-right font-semibold text-slate-700">{debtorView ? (e._balance > 0 ? <>-{inr(e._balance)}</> : e._balance < 0 ? <>+{inr(-e._balance)}</> : inr(0)) : inr(e._balance)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {entries.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No transactions yet</div>}
    </Card>
  );
}

function PartyListPanel({ parties, selectedId, onSelect, search, onSearch, placeholder }) {
  return (
    <Card className="p-0 overflow-hidden">
      <div className="p-3 border-b border-slate-100">
        <Input value={search} onChange={e => onSearch(e.target.value)} placeholder={placeholder || "Search"} />
      </div>
      <div className="max-h-[520px] overflow-y-auto">
        {parties.map(p => (
          <button key={p.id} onClick={() => onSelect(p.id)}
            className={`w-full text-left px-4 py-3 border-b border-slate-50 flex items-center justify-between hover:bg-slate-50 ${selectedId === p.id ? "bg-amber-50" : ""}`}>
            <span className="text-sm font-medium text-slate-800 truncate">{p.name}</span>
            <span className={`text-xs font-semibold shrink-0 ml-2 ${p.amount > 0 ? "text-emerald-600" : "text-slate-400"}`}>{inr(p.amount)}</span>
          </button>
        ))}
        {parties.length === 0 && <div className="p-6 text-center text-sm text-slate-400">No parties found</div>}
      </div>
    </Card>
  );
}

// ── Owner: My Transactions (Owner ↔ Distributor) ──
export function OwnerMyTransactionsPage() {
  const [raw, setRaw] = useState({ entries: [], summary: [] });
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  useEffect(() => { dms.primaryLedger().then(setRaw).catch(() => {}); }, []);
  useEffect(() => { if (!selected && raw.summary.length) setSelected(raw.summary[0].distributor_id); }, [raw]);
  const parties = raw.summary.filter(s => (s.distributor_name || "").toLowerCase().includes(search.toLowerCase()));
  const entries = raw.entries.filter(e => e.distributor_id === selected);
  const sel = raw.summary.find(s => s.distributor_id === selected);
  return (
    <div>
      <PageHeader title="My Transactions" subtitle="Owner ↔ Distributor" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <PartyListPanel parties={parties.map(p => ({ id: p.distributor_id, name: p.distributor_name, amount: p.outstanding }))}
          selectedId={selected} onSelect={setSelected} search={search} onSearch={setSearch} placeholder="Search Distributor" />
        <div className="md:col-span-2">
          {sel ? (<><Card className="p-4 mb-3"><div className="font-semibold text-slate-900">{sel.distributor_name}</div></Card><LedgerTable entries={entries} /></>)
            : <Card className="p-8 text-center text-sm text-slate-500">Select a distributor</Card>}
        </div>
      </div>
    </div>
  );
}

// ── Owner: All Transactions (any Distributor ↔ Retailer) ──
export function OwnerAllTransactionsPage() {
  const [distributors, setDistributors] = useState([]);
  const [distId, setDistId] = useState("");
  const [raw, setRaw] = useState({ entries: [], summary: [] });
  const [selectedRetailer, setSelectedRetailer] = useState(null);
  const [search, setSearch] = useState("");
  useEffect(() => { dms.listDistributors().then(d => setDistributors(d.data || d || [])); }, []);
  useEffect(() => { dms.secondaryLedger().then(setRaw).catch(() => {}); }, []);
  useEffect(() => { setSelectedRetailer(null); }, [distId]);
  const distEntries = raw.entries.filter(e => e.distributor_id === distId);
  const retailerIdsInDist = new Set(distEntries.map(e => e.retailer_id));
  const parties = raw.summary.filter(s => retailerIdsInDist.has(s.retailer_id) && (s.retailer_name || "").toLowerCase().includes(search.toLowerCase()));
  const entries = distEntries.filter(e => e.retailer_id === selectedRetailer);
  const sel = raw.summary.find(s => s.retailer_id === selectedRetailer);
  return (
    <div>
      <PageHeader title="All Transactions" subtitle="View any Distributor ↔ Retailer ledger" />
      <div className="mb-4 max-w-xs">
        <Label>Distributor</Label>
        <Select value={distId} onValueChange={setDistId}>
          <SelectTrigger><SelectValue placeholder="Choose distributor" /></SelectTrigger>
          <SelectContent>{distributors.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {distId ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <PartyListPanel parties={parties.map(p => ({ id: p.retailer_id, name: p.retailer_name, amount: p.outstanding }))}
            selectedId={selectedRetailer} onSelect={setSelectedRetailer} search={search} onSearch={setSearch} placeholder="Search Retailer" />
          <div className="md:col-span-2">
            {sel ? (<><Card className="p-4 mb-3"><div className="font-semibold text-slate-900">{sel.retailer_name}</div></Card><LedgerTable entries={entries} /></>)
              : <Card className="p-8 text-center text-sm text-slate-500">Select a retailer</Card>}
          </div>
        </div>
      ) : <Card className="p-8 text-center text-sm text-slate-500">Choose a distributor to view their retailers</Card>}
    </div>
  );
}

// ── Distributor: My Transactions (With Owner / With Retailers) ──
export function DistributorMyTransactionsPage() {
  const [tab, setTab] = useState("owner");
  const [ownerData, setOwnerData] = useState({ entries: [], summary: [] });
  const [retData, setRetData] = useState({ entries: [], summary: [] });
  const [selectedRetailer, setSelectedRetailer] = useState(null);
  const [search, setSearch] = useState("");
  useEffect(() => {
    dms.primaryLedger().then(setOwnerData).catch(() => {});
    dms.secondaryLedger().then(setRetData).catch(() => {});
  }, []);
  const parties = retData.summary.filter(s => (s.retailer_name || "").toLowerCase().includes(search.toLowerCase()));
  const retEntries = retData.entries.filter(e => e.retailer_id === selectedRetailer);
  const selRet = retData.summary.find(s => s.retailer_id === selectedRetailer);
  return (
    <div>
      <PageHeader title="My Transactions" subtitle="With Owner or with your Retailers" />
      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab("owner")} className={`px-4 py-2 rounded-full text-sm font-medium transition ${tab === "owner" ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-600"}`}>With Owner</button>
        <button onClick={() => setTab("retailers")} className={`px-4 py-2 rounded-full text-sm font-medium transition ${tab === "retailers" ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-600"}`}>With Retailers</button>
      </div>
      {tab === "owner" ? (
        <LedgerTable entries={ownerData.entries} debtorView />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <PartyListPanel parties={parties.map(p => ({ id: p.retailer_id, name: p.retailer_name, amount: p.outstanding }))}
            selectedId={selectedRetailer} onSelect={setSelectedRetailer} search={search} onSearch={setSearch} placeholder="Search Retailer" />
          <div className="md:col-span-2">
            {selRet ? (<><Card className="p-4 mb-3"><div className="font-semibold text-slate-900">{selRet.retailer_name}</div></Card><LedgerTable entries={retEntries} /></>)
              : <Card className="p-8 text-center text-sm text-slate-500">Select a retailer</Card>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Retailer: My Transactions (With Distributor) ──
export function RetailerMyTransactionsPage() {
  const [data, setData] = useState({ entries: [], summary: [] });
  useEffect(() => { dms.secondaryLedger().then(setData).catch(() => {}); }, []);
  const sel = data.summary[0];
  return (
    <div>
      <PageHeader title="My Transactions" subtitle="With your Distributor" />
      {sel && (
        <Card className="p-4 mb-3 flex items-center justify-between">
          <div className="font-semibold text-slate-900">Outstanding Balance</div>
          <div className={`text-lg font-bold ${sel.outstanding > 0 ? "text-rose-700" : "text-emerald-700"}`}>{inr(sel.outstanding)}</div>
        </Card>
      )}
      <LedgerTable entries={data.entries} debtorView />
    </div>
  );
}
